import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';

import { AFF_ID_PARTNER_MAP, ESources } from '@src/client/shared/config/affIdMap';
import { usePageContext } from '@src/config';
import { VITRINA_CRM_EVENT, type CrmChannel, type CrmEventRequest } from '@src/constants/crmVitrina';
import { ListKeys } from '@src/constants/microcredits';
import { Cookie, CookieKey } from '@src/modules/cookie';
import { useMicrocreditUserDealsState } from '@src/reducers/microcreditUserDeals';
import { BFF_PROXY_API } from '@src/shared/constants/apiRoutes';
import { webWhiteLabelDeniedRoute } from '@src/shared/constants/pageRoutes';
import { isMonoAppRefPage, isWhiteLabelDeniedPage } from '@src/utils/routing';
import { requestInstance } from 'shared/api/requestInstance';

function getCrmVitrinaAppId(listKey: ListKeys, asPath: string): CrmChannel {
  const pathname = asPath.split('?')[0];

  if (listKey === ListKeys.MONO_APP_REF || isMonoAppRefPage(pathname)) {
    return 'web';
  }

  if (listKey === ListKeys.WL_DENIED_PAGE || isWhiteLabelDeniedPage(asPath)) {
    return asPath.includes(webWhiteLabelDeniedRoute) ? 'web' : 'app';
  }

  return '';
}

function getCrmVitrinaMfoFd(appId: CrmChannel, hasDeals: boolean): CrmChannel {
  return hasDeals ? appId : 'web';
}

const DAY_MS = 24 * 60 * 60 * 1000;

function reserve(cookieKey: CookieKey): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const now = Date.now();
  const lastSentAt = Number(Cookie.get(cookieKey) || '');

  if (lastSentAt && now - lastSentAt < DAY_MS) {
    return false;
  }

  Cookie.set(cookieKey, String(now), { expires: 1, path: '/' });

  return true;
}

/** Резервирует отправку показа витрины на 24 часа. */
function reserveCrmVitrinaVisited(): boolean {
  return reserve(CookieKey.MFO_VITRINA_CRM_VISITED);
}

/** Резервирует отправку клика по витрине на 24 часа. */
function reserveCrmVitrinaClicked(): boolean {
  return reserve(CookieKey.MFO_VITRINA_CRM_CLICKED);
}

const getQueryParam = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

type CrmVitrinaRequestContext = {
  appId: CrmChannel;
  mfoFD: CrmChannel;
  isAvitoClient: boolean;
  isAuthenticated: boolean;
};

function useCrmVitrinaRequestContext(): CrmVitrinaRequestContext {
  const { query, asPath } = useRouter();
  const { listKey } = usePageContext();
  const isAuthenticated = Boolean(true);
  const { deals } = useMicrocreditUserDealsState();

  const appId = useMemo(() => getCrmVitrinaAppId(listKey, asPath), [listKey, asPath]);
  const mfoFD = useMemo(() => getCrmVitrinaMfoFd(appId, Boolean(deals?.length)), [appId, deals?.length]);

  const isAvitoClient = useMemo(
    () => getQueryParam(query?.aff_id) === AFF_ID_PARTNER_MAP[ESources.AVITO],
    [query?.aff_id],
  );

  return { appId, mfoFD, isAvitoClient, isAuthenticated };
}

/** Возвращает обработчик CRM-клика по офферу (не чаще раза в 24 часа). */
export function useCrmVitrinaClicked() {
  const { appId, mfoFD, isAvitoClient, isAuthenticated } = useCrmVitrinaRequestContext();

  return useCallback(() => {
    if (isAvitoClient || !isAuthenticated || !reserveCrmVitrinaClicked()) {
      return;
    }

    const body: CrmEventRequest = { eventAction: VITRINA_CRM_EVENT.clicked, appId, mfoFD };

    requestInstance.post(BFF_PROXY_API.crmEvent, body).catch(() => undefined);
  }, [isAvitoClient, isAuthenticated, appId, mfoFD]);
}

/** Отправляет CRM-событие показа витрины (не чаще раза в 24 часа). */
export function useCrmVitrinaVisited(isReady: boolean) {
  const { appId, mfoFD, isAvitoClient, isAuthenticated } = useCrmVitrinaRequestContext();

  useEffect(() => {
    if (!isReady || isAvitoClient || !isAuthenticated || !reserveCrmVitrinaVisited()) {
      return;
    }

    const body: CrmEventRequest = { eventAction: VITRINA_CRM_EVENT.visited, appId, mfoFD };

    requestInstance.post(BFF_PROXY_API.crmEvent, body).catch(() => undefined);
  }, [isReady, isAvitoClient, isAuthenticated, appId, mfoFD]);
}
